import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { HeaderWidgetProps, HeaderWidgetState } from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { themeColor } from "../../../../types/color";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: HeaderWidgetState = {
  // Background
  backgroundColor: themeColor("transparent"),

  // Title styling
  titleColor: themeColor("textPrimary"),
  titleFontSize: 18,
  titleFontWeight: "semibold",
  titleFontFamily: "inherit",

  // Icon styling (shared defaults)
  iconColor: themeColor("textPrimary"),
  iconColorHover: themeColor("tertiary"),
  iconSize: 20,

  // Individual icon overrides
  homeIconSize: 22,
  homeIconColor: undefined,
  sidebarIconSize: 20,
  sidebarIconColor: undefined,
  composeIconSize: 18,
  composeIconColor: undefined,
  cartIconSize: 22,
  cartIconColor: undefined,

  // Button styling
  buttonSize: 44,
  buttonBorderRadius: 8,
  buttonHoverBgColor: themeColor("background"),

  // Badge styling (cart count)
  badgeBgColor: themeColor("primary"),
  badgeTextColor: themeColor("secondary"),
  badgeFontSize: 12,
  badgeMinWidth: 18,

  // Layout
  height: 56,
  paddingX: 16,
  paddingY: 8,
  maxWidth: 768,
};

const editFields: EditField[] = [
  // Layout group
  {
    key: "backgroundColor",
    label: "Background",
    type: "color",
    group: "Layout",
  },
  {
    key: "height",
    label: "Height",
    type: "number",
    min: 40,
    max: 80,
    step: 4,
    group: "Layout",
  },
  {
    key: "paddingX",
    label: "Horizontal Padding",
    type: "number",
    min: 0,
    max: 32,
    step: 4,
    group: "Layout",
  },
  {
    key: "paddingY",
    label: "Vertical Padding",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
    group: "Layout",
  },
  {
    key: "maxWidth",
    label: "Max Width",
    type: "number",
    min: 400,
    max: 1200,
    step: 50,
    group: "Layout",
  },

  // Title group
  { key: "titleColor", label: "Color", type: "color", group: "Title" },
  {
    key: "titleFontSize",
    label: "Font Size",
    type: "number",
    min: 14,
    max: 24,
    step: 1,
    group: "Title",
  },
  {
    key: "titleFontWeight",
    label: "Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
    group: "Title",
  },
  {
    key: "titleFontFamily",
    label: "Font Family",
    type: "font",
    group: "Title",
  },

  // Home Icon group
  {
    key: "homeIconSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 32,
    step: 1,
    group: "Home Icon",
  },
  {
    key: "homeIconColor",
    label: "Color (optional)",
    type: "color",
    group: "Home Icon",
  },

  // Sidebar Icon group
  {
    key: "sidebarIconSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 32,
    step: 1,
    group: "Sidebar Icon",
  },
  {
    key: "sidebarIconColor",
    label: "Color (optional)",
    type: "color",
    group: "Sidebar Icon",
  },

  // Compose Icon group
  {
    key: "composeIconSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 32,
    step: 1,
    group: "Compose Icon",
  },
  {
    key: "composeIconColor",
    label: "Color (optional)",
    type: "color",
    group: "Compose Icon",
  },

  // Cart Icon group
  {
    key: "cartIconSize",
    label: "Size",
    type: "number",
    min: 16,
    max: 32,
    step: 1,
    group: "Cart Icon",
  },
  {
    key: "cartIconColor",
    label: "Color (optional)",
    type: "color",
    group: "Cart Icon",
  },

  // Shared Icons group (default color applies to all without individual override)
  {
    key: "iconColor",
    label: "Default Color",
    type: "color",
    group: "All Icons (Defaults)",
  },
  {
    key: "iconColorHover",
    label: "Hover Color",
    type: "color",
    group: "All Icons (Defaults)",
  },

  // Buttons group
  {
    key: "buttonSize",
    label: "Size",
    type: "number",
    min: 32,
    max: 56,
    step: 4,
    group: "Buttons",
  },
  {
    key: "buttonBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 16,
    step: 2,
    group: "Buttons",
  },
  {
    key: "buttonHoverBgColor",
    label: "Hover Background",
    type: "color",
    group: "Buttons",
  },

  // Badge group
  { key: "badgeBgColor", label: "Background", type: "color", group: "Badge" },
  { key: "badgeTextColor", label: "Text Color", type: "color", group: "Badge" },
  {
    key: "badgeFontSize",
    label: "Font Size",
    type: "number",
    min: 10,
    max: 16,
    step: 1,
    group: "Badge",
  },
  {
    key: "badgeMinWidth",
    label: "Min Width",
    type: "number",
    min: 14,
    max: 24,
    step: 2,
    group: "Badge",
  },
];

export const HeaderWidget: React.FC<HeaderWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<HeaderWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.titleFontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  // Load Google Font into document.head when font changes
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts)
  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  // Generate unique class for this widget instance
  const uniqueClass = `header-${widgetId || "default"}`;

  // Generate @font-face CSS and font-family rule
  const styleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueClass} [data-title-text] { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Create enhanced state with resolved fontFamily CSS
  const enhancedState: HeaderWidgetState & { titleFontFamilyCss?: string } = {
    ...state,
    titleFontFamilyCss: fontFamilyCss,
  };

  // Render the children with current state and font styles
  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      {children(enhancedState, resolveColor)}
    </>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
